import React from 'react';
import '../styles/Terms.css';

const Terms = () => {
  const lastUpdated = 'June 13, 2026';

  const companyName = 'Tallhaven';
  const legalEmail = 'legal@tallhaven.com';

  return (
    <main className="terms-page">
      <header className="terms-hero">
        <div className="container">
          <a href="/" className="terms-back">&larr; Back to Home</a>
          <h1 className="terms-title">Terms &amp; Conditions</h1>
          <p className="terms-updated">Last updated: {lastUpdated}</p>
        </div>
      </header>

      <div className="container terms-content">
        <p>
          These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of
          websites, mobile applications, games, software tools, AI-powered products, subscriptions,
          digital content, and related services provided by {companyName} (&ldquo;{companyName}
          &rdquo;, &ldquo;we&rdquo;, &ldquo;us&rdquo;, or &ldquo;our&rdquo;). These products and
          services are collectively referred to as the &ldquo;Services.&rdquo;
        </p>

        <p>
          By downloading, installing, accessing, creating an account, purchasing, subscribing to, or
          using any part of the Services, you agree to be bound by these Terms. If you do not agree,
          do not use the Services.
        </p>

        <section>
          <h2>1. Eligibility</h2>
          <p>
            You must be at least 13 years old, or the minimum age required in your jurisdiction, to
            use the Services. If you are under the age of majority in your jurisdiction, you may use
            the Services only with the involvement and consent of a parent or legal guardian. By using
            the Services, you represent that you meet these requirements.
          </p>
        </section>

        <section>
          <h2>2. License to Use the Services</h2>
          <p>
            Subject to your compliance with these Terms, we grant you a limited, non-exclusive,
            non-transferable, non-sublicensable, revocable license to access and use the Services for
            your personal or internal business use, depending on the nature of the Service.
          </p>

          <p>
            You may not copy, sell, resell, lease, distribute, modify, reverse engineer, decompile, or
            attempt to derive the source code of any part of the Services, except where such
            restriction is prohibited by applicable law.
          </p>

          <p>
            If you download an application through a third-party marketplace, such as the Apple App
            Store or Google Play, your use is also subject to that marketplace&rsquo;s terms,
            conditions, and usage rules.
          </p>
        </section>

        <section>
          <h2>3. Accounts</h2>
          <p>
            Certain features may require you to create an account. You agree to provide accurate,
            current, and complete information and to keep your login credentials secure. You are
            responsible for all activity that occurs under your account.
          </p>

          <p>
            We may suspend or terminate accounts that violate these Terms, misuse the Services, create
            risk for other users, or violate applicable laws or third-party rights.
          </p>
        </section>

        <section>
          <h2>4. Acceptable Use</h2>
          <p>You agree that you will not, and will not permit others to:</p>

          <ul>
            <li>Use the Services for any unlawful, harmful, abusive, or fraudulent purpose;</li>
            <li>Violate any applicable law, regulation, intellectual property right, privacy right, or other third-party right;</li>
            <li>Upload, create, transmit, or share content that is illegal, obscene, sexually explicit, hateful, harassing, threatening, defamatory, or otherwise harmful;</li>
            <li>Use the Services to generate, distribute, or promote malware, spam, scams, deception, or misleading content;</li>
            <li>Interfere with, disrupt, overload, or attempt to gain unauthorized access to the Services or related systems;</li>
            <li>Use bots, scripts, automation, scraping, or other automated means in a way that harms, abuses, or excessively burdens the Services;</li>
            <li>Cheat, exploit bugs, manipulate gameplay, modify game clients, or interfere with fair play in any game or multiplayer feature;</li>
            <li>Impersonate another person, misrepresent your identity, or falsely imply an affiliation with any person or organization;</li>
            <li>Use the Services in a way that could damage our reputation, security, systems, users, or third-party partners.</li>
          </ul>
        </section>

        <section>
          <h2>5. User Content</h2>
          <p>
            The Services may allow you to upload, submit, create, store, display, or transmit content,
            including images, prompts, text, usernames, profile information, gameplay data, feedback,
            or other materials (&ldquo;User Content&rdquo;).
          </p>

          <p>
            You retain ownership of your User Content. By submitting User Content to the Services, you
            grant us a worldwide, non-exclusive, royalty-free license to host, store, process,
            reproduce, display, transmit, and use your User Content solely as needed to operate,
            improve, secure, and provide the Services.
          </p>

          <p>
            You are responsible for your User Content and represent that you have all rights necessary
            to submit it. We may remove or restrict User Content that violates these Terms, applicable
            law, or the rights of others.
          </p>
        </section>

        <section>
          <h2>6. AI Features and Generated Output</h2>
          <p>
            Some Services may include AI-powered features that generate, transform, edit, analyze, or
            suggest content based on your inputs. You are responsible for the prompts, images, files,
            or other materials you submit and for how you use any generated output.
          </p>

          <p>
            AI-generated output may be inaccurate, incomplete, offensive, similar to content generated
            for other users, or unsuitable for your intended use. You should review generated output
            before using, publishing, relying on, or commercializing it.
          </p>

          <p>
            We do not guarantee that generated output will be unique, copyrightable, error-free, or
            free from third-party claims. You are responsible for ensuring that your use of generated
            output complies with applicable laws, platform rules, and third-party rights.
          </p>
        </section>

        <section>
          <h2>7. Games, Multiplayer, and Gameplay</h2>
          <p>
            Some Services may include games, online matches, local or network multiplayer, leaderboards,
            virtual items, rankings, progression, or other gameplay features. We may change, balance,
            update, reset, suspend, or discontinue gameplay features at any time.
          </p>

          <p>
            You agree not to cheat, exploit vulnerabilities, interfere with other players, use
            unauthorized modifications, manipulate network activity, or otherwise abuse game systems.
            We may restrict access to gameplay features if we believe your behavior harms the game,
            other users, or the integrity of the Services.
          </p>
        </section>

        <section>
          <h2>8. Purchases, Subscriptions, and Billing</h2>
          <p>
            Some Services may offer paid features, subscriptions, credits, in-app purchases, virtual
            items, or other paid digital content. Prices, features, and availability may change over
            time.
          </p>

          <p>
            If you purchase through a third-party marketplace, such as the Apple App Store or Google
            Play, your purchase is processed by that marketplace and is subject to its payment,
            refund, cancellation, and subscription terms.
          </p>

          <p>
            Subscriptions may automatically renew unless canceled before the renewal date. You can
            manage or cancel subscriptions through the account, platform, app store, or payment
            provider used to make the purchase. Except where required by law or applicable platform
            policy, payments are non-refundable.
          </p>
        </section>

        <section>
          <h2>9. Third-Party Services</h2>
          <p>
            The Services may include links, integrations, APIs, hosting providers, analytics tools,
            payment processors, app marketplaces, AI providers, advertising platforms, or other
            third-party services that we do not control.
          </p>

          <p>
            We are not responsible for third-party services, content, policies, availability, or
            practices. Your use of third-party services is governed by their own terms and privacy
            policies.
          </p>
        </section>

        <section>
          <h2>10. Privacy</h2>
          <p>
            Your use of the Services is also governed by our{' '}
            <a href="/privacy">Privacy Policy</a>, which explains how we collect, use, disclose, and
            protect information. By using the Services, you agree to the data practices described in
            the Privacy Policy.
          </p>
        </section>

        <section>
          <h2>11. Intellectual Property</h2>
          <p>
            The Services, including software, code, designs, graphics, game characters, gameplay
            systems, logos, trademarks, text, interfaces, visual assets, and other materials, are
            owned by us or our licensors and are protected by intellectual property laws.
          </p>

          <p>
            Except for the limited license granted to you under these Terms, no rights are transferred
            to you. You may not use our names, logos, branding, or trademarks without our prior
            written permission.
          </p>
        </section>

        <section>
          <h2>12. Feedback</h2>
          <p>
            If you submit suggestions, ideas, bug reports, feature requests, or other feedback, you
            grant us the right to use that feedback without restriction or compensation to you. We may
            use feedback to improve, modify, market, or develop the Services.
          </p>
        </section>

        <section>
          <h2>13. Service Changes and Availability</h2>
          <p>
            We may update, modify, suspend, limit, or discontinue any part of the Services at any time.
            We do not guarantee that the Services will always be available, uninterrupted, secure, or
            error-free.
          </p>

          <p>
            We may release updates that add, remove, change, rebalance, or limit features, including
            gameplay systems, AI tools, pricing, usage limits, subscriptions, or generated content
            features.
          </p>
        </section>

        <section>
          <h2>14. Disclaimers</h2>
          <p>
            THE SERVICES ARE PROVIDED &ldquo;AS IS&rdquo; AND &ldquo;AS AVAILABLE&rdquo; WITHOUT
            WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING WITHOUT
            LIMITATION WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE,
            NON-INFRINGEMENT, ACCURACY, AVAILABILITY, OR RELIABILITY.
          </p>

          <p>
            WE DO NOT WARRANT THAT THE SERVICES, GAMEPLAY, AI OUTPUTS, DIGITAL CONTENT, OR THIRD-PARTY
            INTEGRATIONS WILL BE UNINTERRUPTED, SECURE, ERROR-FREE, ACCURATE, OR SUITABLE FOR YOUR
            INTENDED USE. SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OF CERTAIN WARRANTIES, SO
            SOME OF THE ABOVE MAY NOT APPLY TO YOU.
          </p>
        </section>

        <section>
          <h2>15. Limitation of Liability</h2>
          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, {companyName} AND ITS AFFILIATES, OWNERS,
            EMPLOYEES, CONTRACTORS, LICENSORS, AND SERVICE PROVIDERS WILL NOT BE LIABLE FOR ANY
            INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES, OR FOR ANY
            LOSS OF PROFITS, REVENUE, DATA, GOODWILL, CONTENT, BUSINESS OPPORTUNITY, OR DEVICE
            FUNCTIONALITY ARISING OUT OF OR RELATING TO YOUR USE OF THE SERVICES.
          </p>

          <p>
            TO THE MAXIMUM EXTENT PERMITTED BY LAW, OUR TOTAL LIABILITY FOR ANY CLAIM RELATING TO THE
            SERVICES WILL NOT EXCEED THE GREATER OF THE AMOUNT YOU PAID TO US FOR THE SERVICES IN THE
            TWELVE MONTHS BEFORE THE CLAIM OR USD $100.
          </p>
        </section>

        <section>
          <h2>16. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless {companyName} and its affiliates,
            owners, employees, contractors, licensors, and service providers from any claims, damages,
            liabilities, losses, costs, and expenses, including reasonable legal fees, arising out of
            or related to your use of the Services, your User Content, your generated output, your
            violation of these Terms, or your violation of any law or third-party right.
          </p>
        </section>

        <section>
          <h2>17. Termination</h2>
          <p>
            We may suspend or terminate your access to the Services at any time, with or without
            notice, if we believe you have violated these Terms, created risk or harm, misused the
            Services, or if we discontinue all or part of the Services.
          </p>

          <p>
            Upon termination, the licenses granted to you will end and you must stop using the
            Services. Sections that by their nature should survive termination will continue to apply,
            including intellectual property, disclaimers, limitation of liability, indemnification,
            and dispute provisions.
          </p>
        </section>

        <section>
          <h2>18. Governing Law and Disputes</h2>
          <p>
            These Terms are governed by the laws of the jurisdiction in which {companyName} is
            established, without regard to conflict-of-law rules. Any dispute will be subject to the
            exclusive jurisdiction of the competent courts of that jurisdiction, unless mandatory
            consumer-protection laws in your country or state of residence provide otherwise.
          </p>

          <p>
            Nothing in these Terms limits any rights you may have under applicable consumer-protection
            laws.
          </p>
        </section>

        <section>
          <h2>19. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. When we make material changes, we will update
            the &ldquo;Last updated&rdquo; date above and, where appropriate, provide additional
            notice. Your continued use of the Services after changes take effect constitutes
            acceptance of the revised Terms.
          </p>
        </section>

        <section className="terms-apple">
          <h2>20. Apple App Store &mdash; Additional Terms</h2>
          <p>
            The following additional terms apply if you download an application from the Apple App
            Store:
          </p>

          <ul>
            <li>
              <strong>Acknowledgement:</strong> These Terms are concluded between you and {companyName}
              only, and not with Apple. Apple is not responsible for the application or its content.
            </li>
            <li>
              <strong>Scope of License:</strong> The license granted to you is limited to a
              non-transferable license to use the application on Apple-branded products that you own
              or control, as permitted by the Apple Media Services Terms and Conditions.
            </li>
            <li>
              <strong>Maintenance and Support:</strong> {companyName} is solely responsible for
              providing maintenance and support for the application. Apple has no obligation to
              provide maintenance or support.
            </li>
            <li>
              <strong>Warranty:</strong> In the event of any failure of the application to conform to
              any applicable warranty, you may notify Apple, and Apple may refund the purchase price,
              if any, for the application. To the maximum extent permitted by law, Apple has no other
              warranty obligation with respect to the application.
            </li>
            <li>
              <strong>Product Claims:</strong> {companyName}, not Apple, is responsible for addressing
              claims relating to the application or your use of it.
            </li>
            <li>
              <strong>Intellectual Property Claims:</strong> {companyName}, not Apple, is responsible
              for the investigation, defense, settlement, and discharge of any third-party
              intellectual-property infringement claim relating to the application.
            </li>
            <li>
              <strong>Legal Compliance:</strong> You represent that you are not located in a country
              subject to a U.S. Government embargo or designated as a terrorist-supporting country,
              and that you are not listed on any U.S. Government list of prohibited or restricted
              parties.
            </li>
            <li>
              <strong>Third-Party Beneficiary:</strong> Apple and Apple&rsquo;s subsidiaries are
              third-party beneficiaries of these Terms and may enforce these Terms against you.
            </li>
          </ul>
        </section>

        <section className="terms-google">
          <h2>21. Google Play &mdash; Additional Terms</h2>
          <p>
            If you download an application from Google Play, your use is also subject to the Google
            Play Terms of Service and applicable Google Play policies. You acknowledge that Google is
            not responsible for the application or its content, and that Google Play terms govern the
            relationship between you and Google with respect to obtaining the application.
          </p>
        </section>

        <section>
          <h2>22. Contact Us</h2>
          <p>
            If you have questions about these Terms, contact us at{' '}
            <a href={`mailto:${legalEmail}`}>{legalEmail}</a>.
          </p>
        </section>

        <p className="terms-footer-note">
          &copy; 2026 {companyName}. All rights reserved.
        </p>
      </div>
    </main>
  );
};

export default Terms;